import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Lock, User } from "lucide-react";

export default function Login() {
  const { login, error } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const ok = await login(username, password);
    setBusy(false);
    if (ok) navigate("/");
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Cover */}
      <div className="auth-cover hidden lg:flex flex-col justify-between p-12 text-white">
        <img src="/intenciv-logo.webp" alt="IntenCiv Diagnostics" className="h-12 object-contain self-start bg-white/95 rounded-lg px-4 py-2" />
        <div>
          <p className="overline text-teal mb-3">Laboratory Personnel & Competence</p>
          <h1 className="font-serif-display text-4xl leading-tight tracking-tight max-w-md">
            The single source of truth for your lab's workforce lifecycle.
          </h1>
          <p className="text-white/70 mt-4 max-w-md text-sm leading-relaxed">
            Employee master, documents, training, competency and authorization — tracked as independent,
            ISO 15189:2022-oriented states. From hire to relieving.
          </p>
        </div>
        <p className="text-white/40 text-xs">IntenCiv Diagnostics · Jaipur, Rajasthan</p>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-white">
        <div className="w-full max-w-sm fade-in">
          <div className="lg:hidden mb-8">
            <img src="/intenciv-logo.webp" alt="IntenCiv Diagnostics" className="h-10 object-contain" />
          </div>
          <p className="overline text-teal mb-2">Secure Sign-in</p>
          <h2 className="font-serif-display text-3xl tracking-tight text-navy mb-1">Welcome back</h2>
          <p className="text-sm text-slate-500 mb-8">Sign in to the HR &amp; Competency console.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="username" className="text-slate-700">Employee ID or Email</Label>
              <div className="relative mt-1.5">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input id="username" type="text" required value={username} onChange={(e) => setUsername(e.target.value)}
                  placeholder="INT0001" className="pl-9 focus-visible:ring-teal" data-testid="login-email" />
              </div>
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••" className="pl-9 focus-visible:ring-teal" data-testid="login-password" />
              </div>
            </div>

            {error && <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="login-error">{error}</p>}

            <Button type="submit" disabled={busy} data-testid="login-submit"
              className="w-full bg-navy hover:bg-navy-700 text-white btn-lift h-11">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
          <p className="text-xs text-slate-400 mt-6 text-center">
            Protected by JWT session security. Accounts lock after repeated failed attempts.
          </p>
        </div>
      </div>
    </div>
  );
}
